import React, { useState } from 'react';
import {
  Button,
  TextField,
  MenuItem,
  Grid,
  Paper,
  FormControlLabel,
  Checkbox,
  Typography,
  useTheme,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { fetchPost } from '../../../helpers/fetching';
import { useStyles } from '../Auth.style';
import Logo from '../../../resources/coorporate_images/brayz_logo_login.png';
import { CopyrightShort } from '../common/CopyrightShort';
import MediaLinks from '../common/MediaLinks';
import NeuroConnector from '../../../resources/coorporate_images/neuro_network_small.png';
import { Link as RouterLink } from 'react-router-dom';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VisibilityIcon from '@material-ui/icons/Visibility';

export default function RegisterForm() {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();

  const { user_settings } = useSelector((state) => state.global_settings);
  const darkState = useSelector((state) => state.gui.darkState);
  const [serverError, setServerError] = useState('');
  const [serverInfo, setServerInfo] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const initialLanguage = user_settings?.language || 'en';

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      username: '',
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      repeat: '',
      role: 'technician',
      aliases: '',
      qa_responsible: false,
      language: initialLanguage,
      created_at: new Date().toISOString(),
    },
  });

  const role = watch('role');

  const onSubmit = async (data) => {
    setServerError('');
    setServerInfo('');
    try {
      const res = await fetchPost('users', data);
      if (!res.success) throw new Error(res.msg);

      const text = t('LoginRegister.notification', {
        role: data.role,
        username: data.username,
      });

      await fetchPost('notifications', {
        text,
        recipient: 'admin',
      });

      setServerInfo(`${t(`api_messages.${res.msg}`)}` || t('api_messages.account_created'));

      reset();
    } catch (e) {
      setServerError(e.message || e.msg);
    }
  };

  return (
    <Grid container component='main' className={classes.root}>
      <Grid item xs={3} className={darkState ? classes.image : classes.image_light} />

      <Grid
        container
        direction='column'
        item
        xs={6}
        component={Paper}
        elevation={0}
        square
        alignItems='center'
        justifyContent='space-evenly'
        style={{
          zIndex: '1000',
          backgroundColor: theme.palette.background.mainDark,
        }}>
        <img
          className={classes.avatar}
          src={Logo}
          alt='logo missing'
          onClick={() => {
            window.open('/login', '_self');
          }}
        />
        <form onSubmit={handleSubmit(onSubmit)} noValidate className={classes.form} autoComplete='off'>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Controller
              name='language'
              control={control}
              render={({ field }) => (
                <TextField
                  variant='outlined'
                  size='small'
                  {...field}
                  select
                  // style={{ width: '250px' }}
                  label={t('ifu_general.select_language')}
                  fullWidth
                  margin='normal'
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                    i18n.changeLanguage(value);
                  }}>
                  <MenuItem value='en'>English</MenuItem>
                  <MenuItem value='de'>Deutsch</MenuItem>
                  <MenuItem value='es'>Español</MenuItem>
                  <MenuItem value='fr'>Français</MenuItem>
                  <MenuItem value='lt'>Lithuanian</MenuItem>
                  <MenuItem value='pl'>Polski</MenuItem>
                  <MenuItem value='it'>Italiano</MenuItem>
                </TextField>
              )}
            />
          </div>

          {/* Username */}
          <Controller
            name='username'
            control={control}
            rules={{
              required: t('LoginRegister.all_required'),
              validate: (v) => !v.includes('_') || t('LoginRegister.error_underscore'),
            }}
            render={({ field }) => (
              <TextField
                variant='filled'
                {...field}
                label={t('LoginRegister.username')}
                fullWidth
                margin='normal'
                error={!!errors.username}
                helperText={errors.username?.message}
                inputProps={{
                  autoComplete: 'new-username',
                  form: { autoComplete: 'off' },
                }}
              />
            )}
          />

          {/* First / Last */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name='first_name'
                control={control}
                // rules={{ required: t('LoginRegister.all_required') }}
                render={({ field }) => (
                  <TextField
                    variant='filled'
                    {...field}
                    label={t('LoginRegister.first_name')}
                    fullWidth
                    margin='normal'
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name='last_name'
                control={control}
                // rules={{ required: t('LoginRegister.all_required') }}
                render={({ field }) => (
                  <TextField
                    variant='filled'
                    {...field}
                    label={t('LoginRegister.last_name')}
                    fullWidth
                    margin='normal'
                    error={!!errors.last_name}
                    helperText={errors.last_name?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Email */}
          <Controller
            name='email'
            control={control}
            rules={{
              // required: t('LoginRegister.all_required'),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('LoginRegister.error_invalid_email'),
              },
            }}
            render={({ field }) => (
              <TextField
                variant='filled'
                {...field}
                label={t('LoginRegister.email')}
                fullWidth
                margin='normal'
                error={!!errors.email}
                helperText={errors.email?.message}
                inputProps={{
                  autoComplete: 'new-email',
                  form: { autoComplete: 'off' },
                }}
              />
            )}
          />

          <Grid
            container
            spacing={2}
            style={{
              display: 'flex',
              alignItems: 'center',
            }}>
            <Grid item xs={role === 'technician' ? 6 : 12}>
              {/* Role */}
              <Controller
                name='role'
                control={control}
                render={({ field }) => (
                  <TextField
                    variant='filled'
                    {...field}
                    select
                    label={t('LoginRegister.role')}
                    fullWidth
                    margin='normal'>
                    <MenuItem value='technician'>{t('users.Technician')}</MenuItem>
                    <MenuItem value='radiologist'>{t('users.Radiologist')}</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            {/* QA */}
            {role === 'technician' && (
              <Grid
                item
                xs={6}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Controller
                  name='qa_responsible'
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} color='primary' />}
                      label={t('LoginRegister.qa_responsible')}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>

          {/* Aliases */}
          {role === 'technician' && (
            <Controller
              name='aliases'
              control={control}
              render={({ field }) => (
                <TextField
                  variant='filled'
                  {...field}
                  label={t('LoginRegister.aliases')}
                  fullWidth
                  margin='normal'
                />
              )}
            />
          )}

          {/* Password */}
          <Controller
            name='password'
            control={control}
            rules={{
              required: t('LoginRegister.all_required'),
              minLength: { value: 8, message: t('api_messages.password_too_short') },
            }}
            render={({ field }) => (
              <TextField
                variant='filled'
                {...field}
                type={showPassword ? 'text' : 'password'}
                label={t('LoginRegister.password')}
                fullWidth
                margin='normal'
                error={!!errors.password}
                helperText={errors.password?.message}
                inputProps={{
                  autoComplete: 'new-password',
                  form: { autoComplete: 'off' },
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge='end' size='small'>
                      {showPassword ? (
                        <VisibilityOffIcon fontSize='small' />
                      ) : (
                        <VisibilityIcon fontSize='small' />
                      )}
                    </IconButton>
                  ),
                }}
              />
            )}
          />

          {/* Repeat password */}
          <Controller
            name='repeat'
            control={control}
            rules={{
              required: t('LoginRegister.all_required'),
              validate: (v) => v === getValues('password') || t('LoginRegister.not_match'),
            }}
            render={({ field }) => (
              <TextField
                variant='filled'
                {...field}
                type={showRepeatPassword ? 'text' : 'password'}
                label={t('LoginRegister.repeat')}
                fullWidth
                margin='normal'
                error={!!errors.repeat}
                helperText={errors.repeat?.message}
                inputProps={{
                  autoComplete: 'new-password-repeat',
                  form: { autoComplete: 'off' },
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                      edge='end'
                      size='small'>
                      {showRepeatPassword ? (
                        <VisibilityOffIcon fontSize='small' />
                      ) : (
                        <VisibilityIcon fontSize='small' />
                      )}
                    </IconButton>
                  ),
                }}
              />
            )}
          />
          {serverInfo && (
            <div className={classes.serverInfo}>
              <Typography>{serverInfo}</Typography>
            </div>
          )}

          {serverError && (
            <Typography color='error' style={{ margin: 8, display: 'flex', justifyContent: 'center' }}>
              {t(`api_messages.${serverError}`)}
            </Typography>
          )}

          <Button
            type='submit'
            fullWidth
            variant='contained'
            color='primary'
            disabled={isSubmitting}
            style={{ marginTop: 14 }}>
            {t('LoginRegister.register')}
          </Button>
        </form>

        <Typography variant='body2' color='textSecondary' align='center' className={classes.loginInfo}>
          {t('LoginRegister.already_registered')}
          <RouterLink className={classes.register} to='/login'>
            {t('LoginRegister.login')}
          </RouterLink>
        </Typography>

        <Grid
          className={classes.mediaLinks}
          item
          container
          direction='column'
          alignItems='center'
          wrap='nowrap'
          justifyContent='center'
          spacing={1}>
          <Grid item>
            <CopyrightShort />
          </Grid>
          <Grid item>
            <MediaLinks />
          </Grid>
        </Grid>
      </Grid>

      <Grid
        item
        xs={3}
        style={{
          display: 'flex',
          background: darkState
            ? 'linear-gradient(270deg, rgb(95 87 87) 0%, rgb(66, 66, 66) 100%)'
            : 'linear-gradient(270deg, rgb(171 171 171) 0%, rgb(255 255 255) 100%)',
        }}>
        <img className={classes.connector_image} src={NeuroConnector} alt='b_rayz connector' />
      </Grid>
    </Grid>
  );
}
